import React, { useState, useEffect, useCallback } from 'react';
import {
  UnauthorizedError,
  getBusinessPlanStatus,
  downloadBusinessPlanDocx,
  downloadBusinessPlanPdf,
} from '../services/api';

/**
 * BusinessPlanPanel — F3 business-plan generation tracker.
 *
 * Shown while interview_stage is bp_collect / bp_generate / bp_complete.
 * Lists the nine SBA sections with a status chip + preview, a progress bar,
 * per-section Regenerate (routed back through the chat via onRegenerate), and
 * the Word/PDF download buttons once all sections are complete.
 *
 * Polls getBusinessPlanStatus every 5s while bp_status === 'generating', and
 * re-fetches whenever `refreshSignal` changes (App bumps it after each chat
 * turn, so a just-generated/regenerated section shows up immediately).
 */

const STATUS_MESSAGES = {
  collecting: "Let's gather the details we need to write your business plan.",
  generating: 'Writing your business plan, section by section…',
  complete: 'Your business plan is complete.',
};

// Topics covered during bp_collect — shown before generation starts so the user
// knows what Eliza will ask about. Order mirrors the SBA narrative sections.
const BP_TOPICS = [
  ['Market Analysis', 'Who are your customers and how big is the market?'],
  ['Competitive Landscape', 'Who are your competitors and what makes you different?'],
  ['Marketing Strategy', 'How will you find and keep customers?'],
  ['Management Team', 'Who is running the business and what is their background?'],
  ['Growth Strategy', 'How will the business grow in Years 2 and 3?'],
  ['Risk Factors', 'What are the main risks and how will you manage them?'],
];

export default function BusinessPlanPanel({
  sessionId,
  refreshSignal,
  onRegenerate,
  title = 'Business Plan',
  open = true,
  onClose = () => {},
  onOpenSba = null,
  embedded = false,
  onStatusChange = null,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [regenFor, setRegenFor] = useState(null);   // section_id with an open input
  const [instruction, setInstruction] = useState('');
  const [downloading, setDownloading] = useState(null); // 'docx' | 'pdf' | null

  const refresh = useCallback(async () => {
    try {
      const next = await getBusinessPlanStatus(sessionId);
      setData(next);
      // Lift the polled status to App: App's own bpStatus only refreshes on
      // chat turns, so when this panel's 5s poll observes completion FIRST
      // (no further message sent), App would otherwise never learn — and the
      // three-tab panel (incl. SBA Package) wouldn't appear until a manual
      // click. Two sources of truth, now reconciled upward.
      if (onStatusChange && next?.bp_status) onStatusChange(next.bp_status);
    } catch (err) {
      if (!(err instanceof UnauthorizedError)) setError(err.message);
    }
  }, [sessionId, onStatusChange]);

  // Fetch on mount and after each chat turn (refreshSignal).
  useEffect(() => { refresh(); }, [refresh, refreshSignal]);

  // Poll every 5s while generating; stop on any other status or on unmount.
  useEffect(() => {
    if (data?.bp_status !== 'generating') return undefined;
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [data?.bp_status, refresh]);

  // Shared right-panel shell so this fills the slot exactly like DataPanel.
  // When `embedded` (inside RightPanelTabs), render just the body — the tab
  // wrapper supplies the <aside> + header.
  const shell = (body) =>
    embedded ? (
      <div className="data-panel-body">{body}</div>
    ) : (
      <aside
        className={`data-panel ${open ? 'data-panel-open' : 'data-panel-closed'}`}
        aria-label={title}
      >
        <header className="data-panel-header">
          <h2>{title}</h2>
          <button
            type="button"
            className="data-panel-close-btn"
            aria-label="Close panel"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="data-panel-body">{body}</div>
      </aside>
    );

  if (!data) {
    return shell(
      <>
        <p className="bp-panel-status">Loading business-plan status…</p>
        {error && <div className="bp-panel-error">{error}</div>}
      </>
    );
  }

  const { sections, sections_complete: done, sections_total: total, bp_status: status } = data;
  const generating = status === 'generating';
  const allComplete = done === total;

  // During bp_collect, show the list of topics Eliza will ask about (the
  // section-progress view only makes sense once generation has started).
  if (status === 'collecting') {
    return shell(
      <>
        <p className="bp-panel-status">{STATUS_MESSAGES.collecting}</p>
        <p className="bp-topics-intro">
          During this phase, Eliza will ask you about the following topics:
        </p>
        <div className="bp-topics">
          {BP_TOPICS.map(([topic, blurb]) => (
            <div key={topic} className="bp-topic">
              <span className="bp-topic-title">{topic}</span>
              <span className="bp-topic-blurb">{blurb}</span>
            </div>
          ))}
        </div>
        {error && <div className="bp-panel-error">{error}</div>}
      </>
    );
  }
  const firstPendingIdx = sections.findIndex((s) => s.status !== 'complete');
  const pct = total ? Math.round((done / total) * 100) : 0;

  const submitRegen = (section) => {
    onRegenerate(section.title, instruction.trim());
    setRegenFor(null);
    setInstruction('');
  };

  const handleDownload = async (kind) => {
    setDownloading(kind);
    setError(null);
    try {
      if (kind === 'docx') await downloadBusinessPlanDocx(sessionId);
      else await downloadBusinessPlanPdf(sessionId);
    } catch (err) {
      if (!(err instanceof UnauthorizedError)) setError(err.message);
    } finally {
      setDownloading(null);
    }
  };

  return shell(
    <>
      <p className="bp-panel-status">{STATUS_MESSAGES[status] || ''}</p>

      {/* Progress bar */}
      <div className="bp-progress">
        <div className="bp-progress-track">
          <div className="bp-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="bp-progress-label">{done} of {total} sections complete</span>
      </div>

      {/* Sections */}
      <div className="bp-sections">
        {sections.map((s, i) => {
          let chip = s.status; // 'complete' | 'pending'
          if (s.status !== 'complete' && generating && i === firstPendingIdx) {
            chip = 'generating';
          }
          return (
            <div key={s.section_id} className="bp-section">
              <div className="bp-section-head">
                <span className="bp-section-title">{s.title}</span>
                <span className={`bp-chip bp-chip-${chip}`}>{chip}</span>
              </div>

              {s.status === 'complete' && (
                <>
                  {s.preview && <p className="bp-preview">{s.preview}</p>}
                  {regenFor === s.section_id ? (
                    <div className="bp-regen">
                      <input
                        type="text"
                        className="bp-regen-input"
                        placeholder="Any specific instructions? (optional)"
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                      />
                      <div className="bp-regen-actions">
                        <button className="bp-btn" onClick={() => submitRegen(s)}>
                          Regenerate
                        </button>
                        <button
                          className="bp-btn bp-btn-ghost"
                          onClick={() => { setRegenFor(null); setInstruction(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="bp-regen-btn"
                      disabled={generating}
                      title={generating ? 'Wait until the current section finishes' : undefined}
                      onClick={() => setRegenFor(s.section_id)}
                    >
                      Regenerate
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Downloads — only once every section is generated */}
      {allComplete && (
        <div className="bp-downloads">
          <button
            className="bp-btn bp-btn-primary"
            disabled={!!downloading}
            onClick={() => handleDownload('docx')}
          >
            {downloading === 'docx' ? 'Preparing…' : 'Download Word (.docx)'}
          </button>
          <button
            className="bp-btn bp-btn-primary"
            disabled={!!downloading}
            onClick={() => handleDownload('pdf')}
          >
            {downloading === 'pdf' ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
      )}

      {/* Hand-off to the SBA package, once the business plan is complete. */}
      {allComplete && onOpenSba && (
        <div className="bp-downloads">
          <button className="bp-btn" onClick={onOpenSba}>
            Continue to SBA Package →
          </button>
        </div>
      )}

      {error && <div className="bp-panel-error">{error}</div>}
    </>
  );
}
